
import {
  getDetailStation,
  getStationNewId,
  getStationNewName,
  getStationTypes,
  saveStation,
} from "@/service/station.service";
import { defineComponent, isProxy, PropType, ref } from "@vue/runtime-core";
import { ElForm } from "element-plus";
import { AppError } from "@/types/app.type";
import { Station } from "@/types/station.type";
import { CameraForm } from "@/types/camera.type";
import { CameraConfiguration } from "@/types/cameraconfiguration.type";
import { IdName } from "@/types/idname.type";
import { getContainerNames } from "@/service/container.service";
import { getCameras } from "@/service/camera.service";
import { ElMessage } from "element-plus";
import moment from "moment-timezone";

export default defineComponent({
  components: {},
  props: {
    station: {
      type: String,
      required: false,
    },
    stationData: {
      type: Object as PropType<Station>,
    },
  },
  setup() {
    return {
      rules: {},
      entity: ref({} as undefined | Station),
      stationTypes: ref([] as IdName[]),
      containers: ref([] as IdName[]),
      timezones: ref<string[]>(moment.tz.names()),
    };
  },
  data() {
    return {
      value: [],
      cameras: [] as CameraForm[],
      count: 0,
      max: 4,
      min: 0,
    };
  },
  async mounted() {
    // station types
    this.stationTypes = await getStationTypes();

    // cameras
    this.cameras = await getCameras();

    //container names
    this.containers = await getContainerNames();
    this.containers.sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));

    if (isProxy(this.stationData)) {
      // Set entity stationData
      this.entity = Object.assign({}, this.stationData);
      this.count = this.entity.stationsCamera ? this.entity.stationsCamera.length : 0;

      this.initializeCamera(3 - this.count);
      this.setCamerasEntity(this.entity);
    } else if (this.station) {
      this.entity = await getDetailStation(this.station);
      //- cámaras
      this.count = this.entity.stationsCamera ? this.entity.stationsCamera.length : 0;
      this.initializeCamera(3 - this.count);
      this.setCamerasEntity(this.entity);
    } else {
      // - new entity
      this.count = 0;
      this.entity = {} as Station;
      this.entity.office = false;
      this.entity.stationsCamera = [] as CameraConfiguration[];
      this.initializeCamera(3);

      this.entity.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Madrid";
    }
    if (this.entity && !this.station) {
      getStationNewId().then((stationId: string) => {
        if (this.entity) {
          this.entity.station = stationId;
        }
      });
      getStationNewName().then((name: string) => {
        if (this.entity) {
          this.entity.name = name;
        }
      });
    }
  },
  methods: {
    /**
     * do save
     */
    async doSave(): Promise<Station | undefined> {
      if (!this.entity) return;

      const valid = await (this.$refs["form"] as InstanceType<typeof ElForm>).validate();
      if (!valid) throw new Error(AppError.FORM_VALIDATION);

      this.cameras.forEach((cameraNumber) => {
        const camera = Object.assign({}, cameraNumber);
        delete camera.number;
        delete camera.max;
        delete camera.min;
        if (this.entity?.stationsCamera && cameraNumber?.number && cameraNumber?.number >= 1) {
          do {
            const matchingColour = this.entity.stationsCamera.find(
              (camera) =>
                camera.camera?.code === cameraNumber.code &&
                camera.numCamera === cameraNumber.number
            );
            if (!matchingColour) {
              this.entity.stationsCamera.push({
                camera: camera,
                numCamera: cameraNumber.number,
              } as CameraConfiguration);
            }
            cameraNumber.number = cameraNumber.number - 1;
          } while (cameraNumber?.number > 0);
        }
      });
      saveStation(this.entity);

      return this.entity;
    },

    handleChange() {
      this.count = this.cameras.reduce(
        (total: number, camera: CameraForm) => total + (camera.number || 0),
        0
      );

      const available = 3 - this.count;
      // Actualiza el número de cámaras que se puede crear de cada tipo
      this.cameras.forEach((cameraNumber) => {
        if (cameraNumber?.number !== undefined) {
          cameraNumber.max =
            this.count === 3 ? cameraNumber.number : Math.abs(cameraNumber.number + available);
        }
      });

      // Evita que el usuario pueda crear más de tres cámaras.
      if (this.count >= 3) {
        this.count = 3;
        ElMessage.warning(this.$t("warning.camera"));
      }
    },

    /**
     * Incrementa el número de una cámara especifica en la lista de cámaras
     */
    incrementCameraNumber(index: number): void {
      if (this.cameras && index >= 0 && index < this.cameras.length) {
        const camera = this.cameras[index];
        if (camera.number !== undefined) {
          this.cameras[index].number = camera.number + 1;
          // Evita que el usuario pueda borrar una cámara existente
          this.cameras[index].min = this.cameras[index].number;
          // Indica el número de cámaras puede seguir creando
          this.cameras[index].max = this.count === 3 ? camera.number : 3 - this.count;
        }
      }
    },

    /**
     * Recupera el número de cámaras existentes
     */
    setCamerasEntity(station: Station) {
      if (station.stationsCamera && station.stationsCamera.length > 0 && this.cameras) {
        station.stationsCamera.forEach((cameraConfiguration) => {
          const index = this.cameras.findIndex(
            (index: CameraForm) => index.code === cameraConfiguration?.camera?.code
          );

          this.incrementCameraNumber(index);
        });
      }
    },

    initializeCamera(max: number) {
      this.cameras.forEach((camera) => {
        camera.number = 0;
        camera.min = 0;
        camera.max = max;
      });
    },
  },
});
